// Fetches the teacher's school's class sections (GET
// /api/schools/:schoolId/class-sections via schoolsApi — see src/api/http.ts
// and src/api/config.js's SCHOOLS_API_BASE). Class sections don't change in
// real time, so this is a plain REST fetch with no Socket.io subscription
// (unlike useSchoolPasses.js / useStudentPassEvents.js).
//
// The route has no role restriction beyond same-school membership
// (hallpass-backend's class-sections list route), so this returns every
// section in the school, not just ones the calling teacher owns.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { schoolsApi, ApiError } from '../api/http';
import { logError } from '../utils/errorHandling';
import type { components } from '../api/types.gen';

/** A single class section as returned by the class-sections list route. */
export type ClassSection = components['schemas']['ClassSectionResponse'];

type ClassSectionListResponse =
  components['schemas']['ClassSectionListResponse'];

/** Callback that reports whether an in-flight fetch has been cancelled. */
type IsCancelled = () => boolean;

export interface UseClassSectionsResult {
  classSections: ClassSection[];
  loading: boolean;
  error: ApiError | Error | null;
  refetch: (isCancelled?: IsCancelled) => Promise<void>;
}

export function useClassSections(): UseClassSectionsResult {
  const { currentUser } = useAuth();
  const schoolId = currentUser?.schoolId;

  const [classSections, setClassSections] = useState<ClassSection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ApiError | Error | null>(null);

  // Guards against a stale response clobbering state for the wrong
  // schoolId: if schoolId changes while a fetch is in flight, the effect
  // below flips `cancelled` on cleanup so an earlier request that resolves
  // after a later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in useAdminSetupChecklist.js's fetchCounts.
  const fetchClassSections = useCallback(
    async (isCancelled: IsCancelled = () => false): Promise<void> => {
      if (!schoolId) {
        if (!isCancelled()) {
          setClassSections([]);
          setLoading(false);
        }
        return;
      }

      if (!isCancelled()) {
        setLoading(true);
        setError(null);
      }
      try {
        const { data } = await schoolsApi<ClassSectionListResponse>(
          `/api/schools/${schoolId}/class-sections`
        );
        if (!isCancelled()) setClassSections(data);
      } catch (err) {
        logError(err, 'client_api', 'useClassSections');
        if (!isCancelled()) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [schoolId]
  );

  useEffect(() => {
    let cancelled = false;
    fetchClassSections(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchClassSections]);

  return { classSections, loading, error, refetch: fetchClassSections };
}

export default useClassSections;
